import * as Sentry from '@sentry/react';

import {setLogger as setInfinityLogger} from '@pexip/infinity';
import {isLogLevelKey} from '@pexip/logger';
import {setLogger as setMediaLogger} from '@pexip/media';
import {setLogger as setMediaComponentsLogger} from '@pexip/media-components';
import {setLogger as setMediaControlLogger} from '@pexip/media-control';
import {setLogger as setPeerConnectionLogger} from '@pexip/peer-connection';
import {setLogger as setSignalLogger} from '@pexip/signal';

import {loggingConfiguration} from './log/configurationSlot';
import {initializeLogging} from './log/initialize';
import policy from './shared/log-policy.json';

const buildLevel =
    typeof process !== 'undefined' && isLogLevelKey(process.env.LOG_LEVEL)
        ? process.env.LOG_LEVEL
        : 'debug';

// A host may select the level before this module evaluates. Sealing here
// closes the selection for the page load, even when nothing was selected.
const hostConfiguration = loggingConfiguration.seal();
const minLevel = hostConfiguration?.minLevel ?? buildLevel;

/**
 * Whether a host fixed the level for this page load. Branding must not change
 * a level the host selected.
 *
 * @example
 * if (!levelSelectedByHost) {
 *     rootLogger.level = brandingLevel;
 * }
 */
export const levelSelectedByHost = hostConfiguration !== undefined;

// A host persists records itself, so the page keeps no log of its own.
const initialization = initializeLogging({
    policy,
    minLevel,
    retainEvents: hostConfiguration === undefined,
    forward: hostConfiguration?.forward,
    capture: hostConfiguration?.capture,
});

export const loggingAvailable = initialization.status === 'ready';
export const rootLogger = initialization.logger;
/**
 * Redaction for items sent to Sentry: a redacted copy, or `undefined` to drop
 * the item, as well as whether all diagnostic output is withheld.
 */
export const sentryDiagnostics = initialization.diagnostics;

// Expose to window object. With a host there is no log to show or download,
// but the shape stays the same for existing callers.
if (!window.pexDebug) {
    Object.defineProperty(window, 'pexDebug', {
        writable: true,
        value: levelSelectedByHost
            ? {
                  get logEvents() {
                      return [];
                  },
                  dumpLog: () => undefined,
              }
            : {
                  get logEvents() {
                      return rootLogger.logEvents.toArray();
                  },
                  dumpLog: () => rootLogger.downloadLog(),
              },
    });
}

export const logger = rootLogger.child({name: 'infinity-connect'});

const version = process.env.BUILD_HASH_FULL ?? 'BUILD_HASH_FULL missing';
const dirty = process.env.BUILD_DIRTY ? '*' : '';

logger.info(
    {
        buildHash: `${version}${dirty}`,
        ua: navigator.userAgent,
    },
    'Initial log',
);

/**
 * A safe stand-in for an error to report: a new `Error` with a redacted copy
 * of its name, message and stack, so neither the application's error nor its
 * unredacted text reaches Sentry. Anything that is not an `Error` is reported
 * only as a generic error. `undefined` when the copy cannot be redacted.
 *
 * @example
 * const reportable = toReportableError(error);
 * if (reportable) {
 *     Sentry.captureException(reportable);
 * }
 */
const toReportableError = (error: unknown): Error | undefined => {
    let fields: Record<string, unknown>;
    try {
        fields =
            error instanceof Error
                ? {name: error.name, message: error.message, stack: error.stack}
                : {name: 'Error', message: 'Non-Error exception captured'};
    } catch {
        return undefined;
    }
    const redacted = sentryDiagnostics.redact(fields);
    if (!redacted) {
        return undefined;
    }
    const reportable = new Error(
        typeof redacted.message === 'string' ? redacted.message : '',
    );
    reportable.name =
        typeof redacted.name === 'string' ? redacted.name : 'Error';
    reportable.stack =
        typeof redacted.stack === 'string' ? redacted.stack : undefined;
    return reportable;
};

/**
 * A redacted copy of `message`, or `undefined` to drop it.
 *
 * @example
 * toReportableMessage('Joined meet@example.com'); // 'Joined [REDACTED]'
 */
const toReportableMessage = (message: string): string | undefined => {
    const redacted = sentryDiagnostics.redact({message})?.message;
    return typeof redacted === 'string' ? redacted : undefined;
};

/**
 * A redacted copy of an event to report, or `undefined` to drop it.
 *
 * @example
 * toReportableEvent({message: 'Failed', extra: {token: 'abc'}});
 * // {message: 'Failed', extra: {token: '[REDACTED]'}}
 */
const toReportableEvent = (event: unknown): Sentry.Event | undefined =>
    typeof event === 'object' && event !== null
        ? // A redacted JSON copy keeps the shape of the event it copies.
          (sentryDiagnostics.redact(event) as Sentry.Event | undefined)
        : undefined;

const createSentryCapture =
    (moduleName: string, level: Sentry.SeverityLevel) =>
    (context: Record<string, unknown>, msg: string | undefined) => {
        if (!loggingAvailable) {
            return;
        }
        const error = context.error
            ? toReportableError(context.error)
            : undefined;
        const event = context.event
            ? toReportableEvent(context.event)
            : undefined;
        const message = msg ? toReportableMessage(msg) : undefined;
        if (!error && !event && !message) {
            return;
        }
        Sentry.withScope(scope => {
            scope.setTag('module', moduleName);
            scope.setLevel(level);
            if (error) {
                Sentry.captureException(error);
            }
            if (event) {
                Sentry.captureEvent(event);
            }
            if (message) {
                Sentry.captureMessage(message);
            }
        });
    };
const logWithSentry = (
    moduleName: string,
): ReturnType<typeof rootLogger.child> => {
    const logger = rootLogger.child({name: moduleName});
    if (!loggingAvailable) {
        return logger;
    }
    const {fatal, error} = logger;
    const captureFatal = createSentryCapture(moduleName, 'fatal');
    const captureError = createSentryCapture(moduleName, 'error');
    return Object.defineProperties(logger, {
        fatal: {
            value: (
                context: Record<string, unknown>,
                msg: string | undefined,
            ) => {
                captureFatal(context, msg);
                fatal(context, msg);
            },
        },
        error: {
            value: (
                context: Record<string, unknown>,
                msg: string | undefined,
            ) => {
                captureError(context, msg);
                error(context, msg);
            },
        },
    });
};

/**
 * Reports a redacted copy of an exception, only when logging initialization
 * succeeded and redaction is complete. The original error is never passed to
 * Sentry.
 *
 * @example
 * captureException(new Error('Diagnostic failure'));
 */
export const captureException = (error: unknown) => {
    if (!loggingAvailable) {
        return;
    }
    const reportable = toReportableError(error);
    if (reportable) {
        Sentry.captureException(reportable);
    }
};

setPeerConnectionLogger(logWithSentry('peer-connection'));
setMediaControlLogger(logWithSentry('media-control'));
setMediaComponentsLogger(logWithSentry('media-components'));
setMediaLogger(logWithSentry('media'));
setSignalLogger(rootLogger.child({name: 'signal'}));
setInfinityLogger(logWithSentry('infinity'));
